import { createApiClient } from "@trillion/ui/lib/api-client";
import { getEnv } from "@trillion/ui/lib/env";

// Thunk base URL — resolved per request, after bootstrapDevEnv() populates window.__ENV__.
// Same PASSPORT_API_URL the AppAuthProvider uses.
const api = createApiClient(() => getEnv("PASSPORT_API_URL"));

// snake_case to match the API's JSON (JsonNamingPolicy.SnakeCaseLower).
export interface Account {
  // Canonical name (sc-89494): required + non-blank. display_name is derived from these server-side.
  first_name: string;
  last_name: string;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  region?: string | null;
  postal_code?: string | null;
  country?: string | null;
  avatar_url?: string | null;
  /** Read-only: returned by GET /api/account, ignored by PUT. */
  email_verified?: boolean;
}

export interface AvatarUploadResult {
  avatar_url: string;
}

export interface GeoCountry {
  iso2: string;
  name: string;
}

export interface GeoState {
  code: string | null;
  name: string;
}

export interface SuccessResult {
  success: boolean;
}

// GET /auth/invite/validate — neutral { valid:false } hides everything; on valid, prefill fields.
export interface InviteValidation {
  valid: boolean;
  email?: string;
  first_name?: string | null;
  last_name?: string | null;
}

// POST /auth/invite/accept — set password + complete profile (the 8 account fields, no avatar) + token.
export type AcceptInvitePayload = Omit<Account, "avatar_url"> & {
  token: string;
  password: string;
};

// Auth flows that establish a session also return the issued token + user (the cookie is set server-side).
export interface AuthSessionResult {
  token: string;
  user: { id: string; email: string; display_name: string | null };
}

export const passportApi = {
  auth: {
    // Server always responds 200 (no email enumeration) — the page shows a neutral message regardless.
    forgotPassword: (email: string) =>
      api.request<SuccessResult>("/auth/forgot-password", {
        method: "POST",
        body: JSON.stringify({ email }),
      }),
    // A 400 (invalid/expired/used token) makes createApiClient throw with the server body; the page toasts it.
    resetPassword: (token: string, password: string) =>
      api.request<SuccessResult>("/auth/reset-password", {
        method: "POST",
        body: JSON.stringify({ token, password }),
      }),
    // Validate an invite token (public, no auth) — prefills the accept-invite page. ?token= query param.
    validateInvite: (token: string) =>
      api.request<InviteValidation>(
        `/auth/invite/validate?token=${encodeURIComponent(token)}`
      ),
    // Accept an invite — sets password + profile, activates + verifies, auto-logs-in (cookie set server-side).
    // A 400 (invalid/expired/used token) makes createApiClient throw with the server body; the page toasts it.
    acceptInvite: (data: AcceptInvitePayload) =>
      api.request<AuthSessionResult>("/auth/invite/accept", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    // Verify a self-registration email (soft). 400 on invalid/expired/used token.
    verifyEmail: (token: string) =>
      api.request<SuccessResult>("/auth/verify-email", {
        method: "POST",
        body: JSON.stringify({ token }),
      }),
    // Re-send the signed-in user's verification email. No request body. Throws with the server body on
    // 401 / 409 already_verified / 429 (rate limit) / 502 email_send_failed.
    resendVerification: () =>
      api.request<SuccessResult>("/auth/verify-email/resend", { method: "POST" }),
    // Request an email change (authenticated). Emails a confirm link to the NEW address; users.email is
    // unchanged until confirmed. Throws with the server body on 400 same_email / 401 invalid_password /
    // 409 email_taken / 502 email_send_failed so the caller can toast it.
    requestEmailChange: (newEmail: string, password: string) =>
      api.request<SuccessResult>("/auth/email-change/request", {
        method: "POST",
        body: JSON.stringify({ new_email: newEmail, password }),
      }),
    // Confirm an email change (token-gated, no session needed). 400 invalid/expired/used/wrong-purpose,
    // 409 email_taken, 403 account_inactive.
    confirmEmailChange: (token: string) =>
      api.request<SuccessResult>("/auth/email-change/confirm", {
        method: "POST",
        body: JSON.stringify({ token }),
      }),
  },
  account: {
    get: () => api.request<Account>("/api/account"),
    update: (data: Account) =>
      api.request<Account>("/api/account", {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    // Multipart upload — a raw fetch (not createApiClient, which forces a JSON Content-Type).
    // The browser sets multipart/form-data + boundary itself; on non-2xx we throw with the server's
    // body so the caller can surface a useful toast (mirrors createApiClient's error-with-body).
    uploadAvatar: async (blob: Blob): Promise<AvatarUploadResult> => {
      const form = new FormData();
      form.append("file", blob, "avatar.webp");
      const res = await fetch(`${getEnv("PASSPORT_API_URL")}/api/account/avatar`, {
        method: "POST",
        body: form,
        credentials: "include",
      });
      if (!res.ok) {
        const body = await res.text().catch(() => "");
        throw new Error(`API error ${res.status}: ${body || res.statusText}`);
      }
      return res.json();
    },
  },
  geo: {
    // Public reference data — no auth cookie needed.
    countries: () =>
      api.request<GeoCountry[]>("/api/geo/countries"),
    states: (iso2: string) =>
      api.request<GeoState[]>(`/api/geo/countries/${iso2}/states`),
  },
  mfa: {
    // Authenticated account management (the login-flow verify/challenge live in @trillion/auth).
    status: () =>
      api.request<{ required: boolean; recovery_codes_remaining: number }>("/auth/mfa/status"),
    // Regenerate replaces any prior set; codes are returned ONCE (only hashes are stored).
    generateRecoveryCodes: () =>
      api.request<{ recovery_codes: string[] }>("/auth/mfa/recovery-codes", { method: "POST" }),
  },
};
